'use client';

import React from 'react';
import styles from './ButtonHome.module.scss';

export interface ButtonHomeProps {
  /**
   * Custom icon to display in the button
   */
  icon?: React.ReactNode;
  
  /**
   * Button position coordinates
   */
  position?: {
    top?: string | number;
    bottom?: string | number;
    left?: string | number;
    right?: string | number;
  };
  
  /**
   * Size of the button in pixels
   * @default 64
   */
  size?: number;
  
  /**
   * Icon size in pixels
   * @default 24
   */
  iconSize?: number;
  
  /**
   * Click handler function
   */
  onClick?: () => void;

  /**
   * Additional CSS class name
   */
  className?: string;
  
  /**
   * Button description or title (for accessibility)
   */
  title?: string;
}

/**
 * ButtonHome component that displays a floating home button with backdrop blur
 * matching the Figma design from node-id=2002-38293
 */
const ButtonHome: React.FC<ButtonHomeProps> = ({ 
  icon, 
  position = { bottom: '24px', right: '24px' }, 
  size = 64,
  iconSize = 24,
  onClick,
  className,
  title = 'Home'
}) => {
  const buttonStyle: React.CSSProperties = {
    width: size,
    height: size,
    ...position
  };

  const iconStyle: React.CSSProperties = {
    fontSize: iconSize,
    color: '#656565', // Match color from Figma
  };

  return (
    <button
      className={`${styles.buttonHomeWrapper} ${className || ''}`}
      style={buttonStyle}
      onClick={onClick}
      title={title}
      aria-label={title}
      tabIndex={0}
      type="button"
    >
      <span style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      background: 'transparent',
      }}>      {icon ? (
        React.cloneElement(icon as React.ReactElement<React.SVGProps<SVGSVGElement>>, { style: { ...iconStyle, ...(icon as React.ReactElement<React.SVGProps<SVGSVGElement>>).props?.style } })
      ) : (
        // Simple Home SVG icon
        <svg
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        style={iconStyle}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        >
        <path d="M3 11.5L12 4L21 11.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
        <path d="M5 10.5V20H19V10.5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
        </svg>
      )}
      </span>
    </button>
  );
};

export default ButtonHome;
